import { fetchQuery, graphql } from 'relay-runtime';
import moment from 'moment';
import environment from 'api/environment';
import {
  faUsers,
  faFileLines,
  faShieldCheck,
  faClipboardCheck,
  faScrewdriverWrench,
  faCompass,
  faBolt
} from '@fortawesome/pro-regular-svg-icons';
import { SearchResult, SearchSection } from './UniversalSearch';
import { SearchSectionsQueryResponse } from './__generated__/SearchSectionsQuery.graphql';

const query = graphql`
  query SearchSectionsQuery($text: String, $includeCompliance: Boolean!) {
    delegates(filter: { name: $text }, page: { limit: 5 }) {
      edges {
        uuid
        TTC_ID
        firstName
        lastName
        profileImageUrl
        documents(page: { limit: 3 }) {
          edges {
            uuid
            name
            url
            expiryDate
          }
        }
      }
    }
    ttcDelegates: delegates(filter: { ttc_id: $text }, page: { limit: 5 }) {
      edges {
        uuid
        TTC_ID
        firstName
        lastName
        profileImageUrl
        documents(page: { limit: 3 }) {
          edges {
            uuid
            name
            url
            expiryDate
          }
        }
      }
    }
    backgroundChecks(filter: { searchText: $text }, page: { limit: 4 })
      @include(if: $includeCompliance) {
      edges {
        uuid
        approvalDate
        expiresAt
        isRenewal
        isVerification
        onlyDBS
        delegate {
          uuid
          firstName
          lastName
        }
      }
    }
    audits(filter: { searchText: $text }, page: { limit: 4 })
      @include(if: $includeCompliance) {
      edges {
        uuid
        reportNumber
        createdAt
        pdfUrl
        auditTemplate {
          name
          auditType
        }
      }
    }
    machineReports(
      filter: { searchText: $text, sent: true }
      page: { limit: 4 }
    ) @include(if: $includeCompliance) {
      edges {
        uuid
        title
        reportDate
        previewURL
        machine {
          name
        }
      }
    }
  }
`;

// Commands the caller can surface in the search modal (e.g. open a modal).
// Shown as a "Quick Actions" section: all of them on an empty query, then
// filtered by name/keyword match as the user types.
export type SearchAction = {
  name: string;
  keywords: string[];
  onSelect: () => void;
};

// App pages offered as "jump to" results when the query matches their name
const appPages = [
  { name: 'Dashboard', link: '/app', compliance: false },
  { name: 'Delegates', link: '/app/delegates', compliance: false },
  { name: 'Audits', link: '/app/audits', compliance: true },
  { name: 'Machine Reports', link: '/app/machine-reports', compliance: true },
  {
    name: 'Background Checks',
    link: '/app/background-checks',
    compliance: true
  },
  {
    name: 'Security Programmes',
    link: '/app/security-programmes',
    compliance: true
  }
];

const backgroundCheckStatus = (
  approvalDate: string | null,
  expiresAt: string | null
): string => {
  if (!approvalDate) return 'In progress';
  if (!expiresAt) return 'Approved';
  const expiry = moment(expiresAt);
  return `${
    expiry.isBefore(moment()) ? 'Expired' : 'Expires'
  } ${expiry.format('DD/MM/YYYY')}`;
};

// Mirrors the "Check type" column on the Background Checks list page
const backgroundCheckType = (
  isRenewal: boolean,
  isVerification: boolean,
  onlyDBS: boolean
): string => {
  if (isRenewal) return 'Renewal';
  if (isVerification) return 'Verification';
  if (onlyDBS) return 'DBS Check';
  return 'Initial';
};

// Green when valid, red when expired, amber while approved/in progress
const backgroundCheckColor = (
  approvalDate: string | null,
  expiresAt: string | null
): string => {
  if (!approvalDate || !expiresAt) return '#EFA14B'; // amber
  return moment(expiresAt).isBefore(moment()) ? '#CB463A' : '#10b73b';
};

export const searchSections = async (
  text: string,
  includeCompliance: boolean,
  actions: SearchAction[] = []
): Promise<SearchSection[]> => {
  const data = (await fetchQuery(environment, query, {
    // Omit the filter entirely on an empty query: an empty-string filter is
    // not a no-op server-side (`ILIKE '%%'` drops NULL columns), and the list
    // pages omit it too.
    text: text.length > 0 ? text : undefined,
    includeCompliance
  })) as SearchSectionsQueryResponse;

  if (!data) {
    console.error('Could not get search data', data);
    return [];
  }

  const sections: SearchSection[] = [];

  const query_ = text.trim().toLowerCase();

  sections.push({
    label: 'Quick Actions',
    icon: faBolt,
    results: actions
      .filter(
        (action) =>
          query_.length === 0 ||
          action.name.toLowerCase().includes(query_) ||
          action.keywords.some((keyword) => keyword.includes(query_))
      )
      .map((action) => ({
        uuid: `action-${action.name}`,
        title: action.name,
        action: action.onSelect
      }))
  });

  if (query_.length > 0) {
    const pageResults = appPages
      .filter(
        (page) =>
          (!page.compliance || includeCompliance) &&
          page.name.toLowerCase().includes(query_)
      )
      .map((page) => ({
        uuid: `page-${page.link}`,
        title: page.name,
        subtitle: 'Jump to page',
        link: page.link
      }));
    sections.push({
      label: 'Pages',
      icon: faCompass,
      results: pageResults
    });
  }

  // Merge name matches and TTC-ID matches, deduped by uuid
  const seen: { [uuid: string]: boolean } = {};
  const delegates = (data.delegates?.edges ?? [])
    .concat(data.ttcDelegates?.edges ?? [])
    .filter((delegate) => {
      if (!delegate || seen[delegate.uuid]) return false;
      seen[delegate.uuid] = true;
      return true;
    })
    .slice(0, 6);

  sections.push({
    label: 'Delegates',
    icon: faUsers,
    results: delegates.map((delegate) => ({
      uuid: delegate?.uuid ?? '',
      title: `${delegate?.firstName} ${delegate?.lastName}`,
      tag: delegate?.TTC_ID ?? undefined,
      link: `/app/delegates/${delegate?.uuid}`,
      avatar: {
        name: `${delegate?.firstName} ${delegate?.lastName}`,
        url: delegate?.profileImageUrl ?? undefined
      }
    }))
  });

  const documents: SearchResult[] = [];
  delegates.forEach((delegate) => {
    (delegate?.documents?.edges ?? []).forEach((document) => {
      if (document) {
        documents.push({
          uuid: document.uuid,
          title: document.name,
          subtitle: `${delegate?.firstName} ${delegate?.lastName}`,
          // Placeholder documents have no file to open — go to the delegate
          // profile instead (href, when present, takes priority over link)
          href: document.url ?? undefined,
          link: `/app/delegates/${delegate?.uuid}`,
          tag: document.expiryDate
            ? moment(document.expiryDate).format('DD/MM/YYYY')
            : undefined,
          tagLabel: 'EXPIRES'
        });
      }
    });
  });
  sections.push({
    label: 'Documents',
    icon: faFileLines,
    results: documents.slice(0, 5)
  });

  if (includeCompliance) {
    sections.push({
      label: 'Background Checks',
      icon: faShieldCheck,
      results: (data.backgroundChecks?.edges ?? []).map((check) => ({
        uuid: check?.uuid ?? '',
        title: `${check?.delegate?.firstName} ${check?.delegate?.lastName}`,
        subtitle: backgroundCheckStatus(
          check?.approvalDate ?? null,
          check?.expiresAt ?? null
        ),
        titleBadge: backgroundCheckType(
          check?.isRenewal ?? false,
          check?.isVerification ?? false,
          check?.onlyDBS ?? false
        ),
        iconColor: backgroundCheckColor(
          check?.approvalDate ?? null,
          check?.expiresAt ?? null
        ),
        link: `/app/background-checks/${check?.uuid}`
      }))
    });

    sections.push({
      label: 'Audits',
      icon: faClipboardCheck,
      results: (data.audits?.edges ?? []).map((audit) => ({
        uuid: audit?.uuid ?? '',
        title: audit?.auditTemplate?.name ?? '',
        subtitle: `Report #${audit?.reportNumber} · ${moment(
          audit?.createdAt
        ).format('DD/MM/YYYY')}`,
        href: audit?.pdfUrl ?? undefined,
        link: '/app/audits'
      }))
    });

    sections.push({
      label: 'Machine Reports',
      icon: faScrewdriverWrench,
      results: (data.machineReports?.edges ?? []).map((report) => ({
        uuid: report?.uuid ?? '',
        title: report?.title ?? '',
        subtitle: [
          report?.machine?.name,
          moment(report?.reportDate).format('DD/MM/YYYY')
        ]
          .filter(Boolean)
          .join(' · '),
        href: report?.previewURL ?? undefined,
        link: '/app/machine-reports'
      }))
    });
  }
  return sections.filter((section) => section.results.length > 0);
};

export default searchSections;
